import { useEffect } from 'react';
import { getAppTitle } from '@/lib/appBrand';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryConfigs } from '@/lib/queryClient';
import { useCurrentOrgId, useCurrentUser } from '../auth';

/**
 * Lightweight polling hook for global unread chat badge.
 * Counts unread conversations assigned to the current user only.
 * Works on ALL pages (not just /chat) via 30s polling.
 * When on /chat, chatRealtimeManager invalidates ['unread-badge'] for instant updates.
 */
export function useUnreadBadge() {
  const { orgId } = useCurrentOrgId();
  const { user } = useCurrentUser();
  const userId = user?.user_id;

  const query = useQuery({
    ...queryConfigs.realtime,
    queryKey: ['unread-badge', orgId, userId],
    queryFn: async () => {
      if (!orgId || !userId) return 0;

      const { count, error } = await supabase
        .from('chat_conversations')
        .select('*', { count: 'exact', head: true })
        .eq('org_id', orgId)
        .eq('assignee_id', userId)
        .gt('unread_count', 0)
        .neq('status', 'archived');

      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!orgId && !!userId,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  });

  // Update page title with unread count
  useEffect(() => {
    const baseTitle = getAppTitle();
    const count = query.data;
    if (count && count > 0) {
      document.title = `(${count}) ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }
  }, [query.data]);

  return query;
}
